import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import useResponsiveLayout from "./useResponsiveLayout";
import useSocket from "./useSocket";
import useChatData from "./useChatData";
import useBlockList from "./useBlockList";

// 初回接続がこの時間内に確立できない場合はエラー表示へ遷移する
const CONNECTION_TIMEOUT_MS = 10000;

export const useChat = () => {
  // Chat States
  const [selectedChatId, setSelectedChatId] = useState(null);
  const [selectedChatInfo, setSelectedChatInfo] = useState(null);
  const [selectedSenderId, setSelectedSenderId] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false)

  //Socket State
  // connecting | connected | reconnecting | error
  const [connectionStatus, setConnectionStatus] = useState("connecting");
  const hasConnectedRef = useRef(false);
  const connectionTimerRef = useRef(null);
  const [retryToken, setRetryToken] = useState(0);

  // UI States
  const [refreshSidebarToggle, setRefreshSidebarToggle] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [showSheet, setShowSheet] = useState(false);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);

  // Memoized current user data
  const currentSystemUser = useMemo(() => {
    const userData = localStorage.getItem("businessSheetData");
    return userData ? JSON.parse(userData) : null;
  }, []);

  // Custom hook for Responsive Layout
  const {
    isMobileView,
    handleSelectUserMobile,
    handleBackToListMobile,
  } = useResponsiveLayout(selectedChatId, setSelectedChatId,setShowSheet);

  // custom hook for socket io
  const socket = useSocket(currentSystemUser);

  // Get chat data and reset function
  const {
    selectedUserSheetData,
    loadingSheet,
    errorSheet,
    resetUserSheetData,
  } = useChatData(selectedSenderId);

  // Block state of the current user (who I have blocked)
  const { blockedUserIds, markBlocked, markUnblocked } = useBlockList();

  // Socket connection status tracking
  useEffect(() => {
    if (!socket) return;

    const clearConnectionTimer = () => {
      if (connectionTimerRef.current) {
        clearTimeout(connectionTimerRef.current);
        connectionTimerRef.current = null;
      }
    };

    const handleConnected = () => {
      console.log("Socket connection established");
      hasConnectedRef.current = true;
      clearConnectionTimer();
      setConnectionStatus("connected");
    };

    const handleDisconnect = (reason) => {
      console.error("Socket disconnected:", reason);
      // 一度接続できていれば再接続中扱いにしてチャットUIを維持する
      setConnectionStatus(
        hasConnectedRef.current ? "reconnecting" : "connecting"
      );
    };

    socket.on("connect", handleConnected);
    socket.on("disconnect", handleDisconnect);

    // 初回接続のみタイムアウト監視する（再接続中は全画面エラーにしない）
    if (!hasConnectedRef.current) {
      connectionTimerRef.current = setTimeout(() => {
        connectionTimerRef.current = null;
        setConnectionStatus((prev) =>
          prev === "connected" ? prev : "error"
        );
      }, CONNECTION_TIMEOUT_MS);
    }

    return () => {
      socket.off("connect", handleConnected);
      socket.off("disconnect", handleDisconnect);
      clearConnectionTimer();
    };
  }, [socket, retryToken]);

  // 接続エラー画面からの再試行
  const retryConnection = useCallback(() => {
    if (!socket) {
      window.location.reload();
      return;
    }

    hasConnectedRef.current = false;
    setConnectionStatus("connecting");
    setRetryToken((prev) => prev + 1);
    socket.connect();
  }, [socket]);

  // Show profile panel when chat info is selected
  useEffect(() => {
    setShowProfile(!!selectedChatInfo);
  }, [selectedChatInfo, selectedChatId]);

  // Action handlers
  const openSheet = useCallback(() => setShowSheet(prev => !prev), []);

  const closeSheet = useCallback(() => {
    setShowSheet(false);
    resetUserSheetData();
  }, [resetUserSheetData]);

  const refreshSidebar = useCallback(() => {
    setRefreshSidebarToggle((prev) => !prev);
  }, []);

  const handleSelectUser = useCallback((chatId, chatInfo) => {
    if (!chatId) return;

    setSelectedChatId(chatId);
    setSelectedChatInfo(chatInfo);
    handleSelectUserMobile();
  }, [handleSelectUserMobile]);

  const handleBackToList = useCallback(() => {
    handleBackToListMobile();
  }, [handleBackToListMobile]);

  return {
    // States
    selectedChatId,
    selectedChatInfo,
    selectedSenderId,
    connectionStatus,
    showProfile,
    showSheet,
    isLoadingMessages,
    isMobileView,
    currentSystemUser,
    refreshSidebarToggle,
    socket,
    selectedUserSheetData,
    loadingSheet,
    errorSheet,
    isAdmin,
    blockedUserIds,

    // Actions
    setSelectedSenderId,
    setIsLoadingMessages,
    handleSelectUser,
    handleBackToList,
    retryConnection,
    openSheet,
    closeSheet,
    refreshSidebar,
    setIsAdmin,
    markBlocked,
    markUnblocked,
  };
};